/** Thin typed fetch wrapper: base URL, cookies, JSON errors (spec §7). */

const DEFAULT_API_URL = 'http://localhost:8787'

export function apiBase(): string {
  const raw = (import.meta.env.VITE_API_URL as string | undefined) || DEFAULT_API_URL
  return raw.replace(/\/+$/, '')
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  constructor(status: number, code: string, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: unknown
  headers?: Record<string, string>
  signal?: AbortSignal
}

export function apiUrl(path: string, query?: Record<string, string | number | boolean | undefined>): string {
  const url = `${apiBase()}${path}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) if (value !== undefined) params.set(key, String(value))
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

export async function toApiError(res: Response): Promise<ApiError> {
  let code = 'http_error'
  let message = res.statusText || `HTTP ${res.status}`
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } }
    if (body.error?.code) code = body.error.code
    if (body.error?.message) message = body.error.message
  } catch {
    // Not JSON: keep the status text.
  }
  return new ApiError(res.status, code, message)
}

export async function rawRequest(path: string, options: RequestOptions = {}, url = apiUrl(path)): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers }
  let body: string | undefined
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(options.body)
  }
  let res: Response
  try {
    res = await fetch(url, {
      method: options.method ?? 'GET',
      headers,
      body,
      credentials: 'include',
      signal: options.signal,
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new ApiError(0, 'network_error', 'Could not reach the server.')
  }
  if (!res.ok) throw await toApiError(res)
  return res
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const res = await rawRequest(path, options)
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}
